"use client";

import {
  type CrhQuestionDocument,
  typesenseSearch,
} from "../../../actions/typesenseSearch";
import type { GetFurtherResourcesByQuestionsRouteReturn } from "../../../app/storyblok/[localeString]/support-hub/api/further-resources/by-questions/route";
import { useContactPageRouterContext } from "../../../helpers/ContactPageRouterContext";
import { useSearchActive } from "../../../helpers/SearchActiveContext";
import { analyticsClick } from "../../../helpers/analytics";
import { canonicalizeIds } from "../../../helpers/canonicalizeIds";
import type { GetFurtherResourcesReturn } from "../../../helpers/data/faqs/getFurtherResources";
import { getTypesenseCollectionName } from "../../../helpers/getTypesenseCollectionName";
import { locales } from "../../../helpers/locales.mjs";
import { useCrhPageSettings } from "../CrhPageContext/CrhPageContext";
import { FurtherResourcesSection } from "../FurtherResourcesSection/FurtherResourcesSection";
import InterestsGroups from "../InterestsGroups/InterestsGroups";
import { Question } from "../Question/Question";
import {
  TypesenseSearchField,
  type TypesenseSearchFieldProps,
} from "../TypesenseSearchField/TypesenseSearchField";
import {
  CosmosButton,
  CosmosIconCloseSmall,
  CosmosIconInfo,
  CosmosSpinner,
  CosmosText,
} from "@cosmos/web-react";
import classNames from "classnames/bind";
import type { Route } from "next";
import Link from "next/link";
import { useCallback, useEffect, useRef, useState } from "react";
import type {
  SearchResponse,
  SearchResponseHit,
} from "typesense/lib/Typesense/Documents";
import styles from "./SearchResultsSection.module.css";

const cx = classNames.bind(styles);

const PER_PAGE = 10;

export type SearchResultsSectionProps = TypesenseSearchFieldProps & {
  searchQuery: string;
};

function getDisplayQuestion(doc: CrhQuestionDocument): string {
  return doc.summary_question ? doc.summary_question : doc.question;
}

function getTopicLabels(
  topics: Array<{ label: string; id: string }> | undefined,
): string[] {
  if (!topics || topics.length === 0) {
    return [];
  }
  return topics.map((topic) => topic.label).filter(Boolean);
}

function CancelSearchLink({ label }: { label?: string }) {
  const contactPageRouter = useContactPageRouterContext();
  const { setSearchActive } = useSearchActive();

  return (
    <Link passHref legacyBehavior href={contactPageRouter.deselectSearchUrl()}>
      <CosmosButton
        size="small"
        kind="link"
        className={cx("cancel-search-button")}
        onClick={() => setSearchActive(false)}
      >
        <CosmosIconCloseSmall slot="icon" />
        {label || "Cancel search"}
      </CosmosButton>
    </Link>
  );
}

export function SearchResultsSection({
  localeString,
  searchQuery,
  ...typesenseSearchFieldProps
}: SearchResultsSectionProps) {
  const contactPageRouter = useContactPageRouterContext();
  const { interestGroups } = contactPageRouter;
  const {
    interestGroupsLabel,
    searchCancelSearchButtonLabel,
    searchLoadingMessageLabel,
    searchLoadingErrorLabel,
    searchNoResultsLabel,
    searchSearchResultsTitleLabel,
  } = useCrhPageSettings();
  const [hits, setHits] = useState<SearchResponseHit<CrhQuestionDocument>[]>(
    [],
  );
  const [totalFound, setTotalFound] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const loadingShownAtRef = useRef<number | null>(null);
  const [furtherResourcesData, setFurtherResourcesData] =
    useState<GetFurtherResourcesReturn | null>(null);
  const furtherResourcesRequestIdRef = useRef<string | null>(null);

  const collection = getTypesenseCollectionName(localeString);

  const furtherResourcesLocale = localeString;

  const lang =
    locales.find((l) => l.localeString === localeString)?.lang ?? "en";

  const searchTypesense = useCallback(
    async (
      query: string,
      perPage: number = PER_PAGE,
    ): Promise<SearchResponse<CrhQuestionDocument>> => {
      return typesenseSearch({
        collection,
        query,
        type: "hybrid",
        perPage,
        lang,
      });
    },
    [collection, lang],
  );

  useEffect(() => {
    if (!searchQuery.trim()) {
      setLoading(false);
      setHits([]);
      setTotalFound(null);
      setError(null);
      return;
    }

    let cancelled = false;
    setError(null);

    // Only show the loading spinner if the fetch takes longer than this
    const LOADING_DELAY_MS = 300;
    // Once visible, keep it on screen for at least this long so it doesn't flash
    const MIN_LOADING_MS = 1000;

    const showLoadingTimer = setTimeout(() => {
      if (!cancelled) {
        setLoading(true);
        loadingShownAtRef.current = Date.now();
      }
    }, LOADING_DELAY_MS);

    searchTypesense(searchQuery.trim(), PER_PAGE)
      .then((result) => {
        if (cancelled) return;
        const applyResults = () => {
          setHits(result.hits ?? []);
          setTotalFound(result.found);
          setLoading(false);
        };
        const shownAt = loadingShownAtRef.current;
        if (shownAt) {
          const elapsed = Date.now() - shownAt;
          const remaining = MIN_LOADING_MS - elapsed;
          if (remaining > 0) {
            setTimeout(applyResults, remaining);
          } else {
            applyResults();
          }
        } else {
          clearTimeout(showLoadingTimer);
          applyResults();
        }
      })
      .catch((err) => {
        if (cancelled) return;
        clearTimeout(showLoadingTimer);
        setError(err instanceof Error ? err.message : "Search failed");
        setHits([]);
        setTotalFound(null);
        setLoading(false);
      });

    return () => {
      cancelled = true;
      clearTimeout(showLoadingTimer);
      loadingShownAtRef.current = null;
    };
  }, [searchQuery, searchTypesense]);

  const FURTHER_RESOURCES_MAX_RESULTS = 5;

  // Fetch further resources for the top search results in a single server action
  useEffect(() => {
    if (!furtherResourcesLocale || hits.length === 0) {
      setFurtherResourcesData(null);
      return;
    }

    // Canonicalize (dedupe + sort) before building the request URL, not
    // after -- Akamai's edge cache keys off the exact URL/query string the
    // browser sends, so this needs to happen client-side for equivalent
    // requests to share a cache entry.
    const questionIds = canonicalizeIds(
      hits.slice(0, FURTHER_RESOURCES_MAX_RESULTS).map((h) => h.document.id),
    );
    const requestId = questionIds.join(",");
    furtherResourcesRequestIdRef.current = requestId;

    const url =
      `/${furtherResourcesLocale}/support-hub/api/further-resources/by-questions` satisfies Route;
    fetch(`${url}?questionIds=${encodeURIComponent(questionIds.join(","))}`)
      .then(
        (response) =>
          response.json() as Promise<GetFurtherResourcesByQuestionsRouteReturn>,
      )
      .then((result) => {
        if (furtherResourcesRequestIdRef.current !== requestId) return;
        setFurtherResourcesData(result.data);
      })
      .catch(() => {
        if (furtherResourcesRequestIdRef.current !== requestId) return;
        setFurtherResourcesData(null);
      });
  }, [furtherResourcesLocale, hits]);

  if (!searchQuery.trim()) {
    return null;
  }

  if (loading) {
    return (
      <div className={cx("container")}>
        <div className={cx("searching-panel")}>
          <CosmosSpinner className={cx("searching-panel-icon")} />
          <CosmosText size="normal" tag="p">
            {searchLoadingMessageLabel || "Loading results..."}
          </CosmosText>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={cx("container")}>
        <CancelSearchLink label={searchCancelSearchButtonLabel ?? undefined} />
        <div className={cx("info-panel")}>
          <CosmosIconInfo className={cx("info-panel-icon")} />
          <CosmosText size="normal" tag="p">
            {searchLoadingErrorLabel ||
              "Something went wrong. Please try again or clear your search."}
          </CosmosText>
        </div>
      </div>
    );
  }

  return (
    <div className={cx("container")}>
      {/* TODO: this buttoned used to be at the top, design is in discussion */}
      {/* <CancelSearchLink label={searchCancelSearchButtonLabel} /> */}

      <div className={cx("search-container")}>
        <TypesenseSearchField
          localeString={localeString}
          {...typesenseSearchFieldProps}
        />
      </div>

      {totalFound === 0 && (
        <div className={cx("info-panel")}>
          <CosmosIconInfo className={cx("info-panel-icon")} />
          <CosmosText size="normal" tag="p">
            {searchNoResultsLabel || "No matching questions found"}
          </CosmosText>
        </div>
      )}

      {/* 
      <div>
        {totalFound === 0 ? (
          <>No results found for &ldquo;{searchQuery}&rdquo;</>
        ) : (
          <>
            {totalFound} result{totalFound !== 1 ? "s" : ""} for &ldquo;
            {searchQuery}&rdquo;
          </>
        )}
      </div>   
      */}

      {totalFound === 0 ? null : (
        <div className={cx("results-container")}>
          <div>
            <CosmosText
              size="small"
              tag="h2"
              weight="medium"
              className={cx("results-title")}
            >
              {searchSearchResultsTitleLabel || "Search Results"}
              {searchQuery.trim() && <>: &ldquo;{searchQuery.trim()}&rdquo;</>}
            </CosmosText>
            <CancelSearchLink
              label={searchCancelSearchButtonLabel ?? undefined}
            />
            <ol className={cx("results-list")} role="list">
              {hits.map((hit) => (
                <li key={hit.document.id}>
                  <Question
                    question={getDisplayQuestion(hit.document)}
                    tags={getTopicLabels(hit.document.crh_topics)}
                    href={contactPageRouter.selectQuestionUrl({
                      uuid: hit.document.id,
                    })}
                    onClick={() => {
                      analyticsClick({
                        value: `search results | ${getDisplayQuestion(hit.document)} | ${getTopicLabels(hit.document.crh_topics).join(", ")}`,
                        clickUrl: null,
                        elementPosition: "page body",
                        method: "Outbound: FALSE",
                        contentType: "question",
                      });
                    }}
                  />
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}

      {furtherResourcesData?.furtherResources &&
        furtherResourcesData.furtherResources.length > 0 && (
          <FurtherResourcesSection
            knowledgeRailTitle={furtherResourcesData.knowledgeRailTitle}
            furtherResourcesTitle={furtherResourcesData.furtherResourcesTitle}
            furtherResources={furtherResourcesData.furtherResources}
          />
        )}

      {interestGroups.length > 0 && (
        <div className={cx("interest-groups-mobile")}>
          <InterestsGroups
            interestGroupsLabel={interestGroupsLabel ?? ""}
            interests={interestGroups}
            dragProgress={0}
            hasMobileCarousel={true}
            isTopOfPage={false}
            appearance="dark"
            hideTitle={false}
          />
        </div>
      )}
    </div>
  );
}
